import { SubmitClaim } from "commonAssets/components/Forms/SubmitClaim";
import formDefault from "../../docs/formDefault.json";

/**
 * Address Ops records on a paper claim when the claimant supplied no email.
 * A class member carrying this address has no reachable claimant inbox, so the
 * edit-claim OTP is bypassed for Ops on the paper path.
 */
const PAPER_CLAIM_EMAIL = "paperclaim@veteransdiscriminationcase.com";

/**
 * Static value that authorizes the reusable Ops-issued claimant link.
 * Distributed in the link itself, so it is treated as an access gate for the
 * normal claimant flow rather than as a secret: the Notice ID and
 * confirmation code still identify the class member.
 */
const AUTHORIZED_CLAIM_VALUE = "7XAGNQjx0FRP03IuD6C5Zj5s";

/**
 * Ops-only paper-claim entry point (?paper_claim=true).
 *
 * @returns {boolean} True when the current URL requests the paper-claim path.
 */
function isPaperClaim() {
    return (
        new URLSearchParams(window.location.search).get("paper_claim") ===
        "true"
    );
}

/**
 * Authorized claimant entry point (?claim_authorized=<AUTHORIZED_CLAIM_VALUE>).
 *
 * @returns {boolean} True when the current URL carries the exact authorization
 * value. Any other value, including a missing one, is not authorized.
 */
function isAuthorizedClaim() {
    return (
        new URLSearchParams(window.location.search).get("claim_authorized") ===
        AUTHORIZED_CLAIM_VALUE
    );
}

export class customSubmitClaim extends SubmitClaim {
    async form() {
        const form = formDefault.claim_form[this.lang];
        this.init_claim(form);
    }

    init_claim(form) {
        // Ops paper-claim entry and the authorized claimant link both run the
        // standard Notice ID + confirmation code guard, so every claim filed
        // through them is tied to a known class member and the selected
        // environment's deadline API stays authoritative. The front-facing
        // (naked) path stays closed. Paper-only behavior remains keyed to
        // isPaperClaim() alone, so a URL carrying both parameters is still
        // handled as paper.
        if (isPaperClaim() || isAuthorizedClaim()) {
            super.init_claim(form);
            return;
        }

        this.claim.api
            .claimsDeadlineGet({ domain: this.claim.data.domain })
            .then((result) => {
                // Error Handling
                if (result.data.error) {
                    this.setup_result_data_error(result);
                    return;
                }

                this.setup_result_data_error({
                    data: {
                        error: "The deadline to submit a Claim has passed.",
                    },
                });
            })
            .catch((error) => {
                this.errorHandler.log_exception(
                    new Error("Error fetching claim deadline", { cause: error })
                );
                this.errorHandler.display(error);
            });
    }

    guard(params, getDeadline) {
        const node = super.guard(params, getDeadline);
        node.querySelector("#claim_form_alert")?.remove();
        // The guard copy addresses a claimant reading their mailed notice,
        // which is noise for Ops keying a paper claim. Removed from the
        // rendered node only, so claimants keep it if the portal reopens.
        if (isPaperClaim()) {
            node.querySelector("#notice-confirmation p")?.remove();
        }
        return node;
    }

    /**
     * Bypasses the edit-claim OTP for Ops paper entry.
     *
     * The OTP is mailed to the class member's address on record, which Ops
     * cannot receive. Members recorded under PAPER_CLAIM_EMAIL have no
     * claimant inbox to verify against, so the form is rendered directly.
     * Every other member keeps the OTP.
     *
     * Member fields arrive nested under class_member when a claim_id exists
     * and flat when it does not, so both shapes are read.
     */
    send_otp(form, container, class_member, claim_deadline) {
        const recordEmail = String(
            class_member?.class_member?.email_address ??
                class_member?.email_address ??
                ""
        )
            .trim()
            .toLowerCase();

        if (isPaperClaim() && recordEmail === PAPER_CLAIM_EMAIL) {
            this.populate_claim_form(
                form,
                container,
                class_member,
                claim_deadline
            );
            return;
        }

        super.send_otp(form, container, class_member, claim_deadline);
    }

    claim_form(params, additionalParams) {
        const node = super.claim_form(params, additionalParams);
        node.querySelector("#claimFormDeadline")?.remove();
        return node;
    }

    /**
     * Keeps ag_PAPER off the confirmation screen.
     *
     * The base builds each row from document.getElementById(key), so a
     * contact key with no matching form field dereferences null. ag_PAPER is
     * submission metadata rather than a field, and is truthy only on the
     * paper path, which is why it crashes there and nowhere else. The copy is
     * shallow, so the posted payload keeps its ag_PAPER source tag.
     */
    confirmation_claim_member(params) {
        const contact = { ...params.contact };
        delete contact.ag_PAPER;
        return super.confirmation_claim_member({ ...params, contact });
    }

    init_claim_inputs() {
        const claimInputsContact =
            "#full_name, #first_name, #middle_initial, #last_name, #title, #business_name, #street_address_1, #street_address_2, #country, #city, #state, #zip_code, #province, #postal_code, #email_address, #confirm_email_address, #phone_number_1, #phone_number_2, #signature, #date, #ssn, #referer_url, #user_language, #ag_SGUID, #ag_TEST";
        const claimInputsFields =
            "#claim-fields input, #claim-fields textarea, #claim-fields select";
        const claimInputsFieldsPayment =
            "#payment_method, #payment_token, #payment_email, #payment_phone";
        const claimInputsTransactions =
            "#transactions_1 input, #transactions_1 textarea, #transactions_1 select, #transactions_2 input, #transactions_2 textarea, #transactions_2 select, #transactions_3 input, #transactions_3 textarea, #transactions_3 select, #transactions_4 input, #transactions_4 textarea, #transactions_4 select";

        this.claim.inputs.contact = Array.from(
            document.querySelectorAll(claimInputsContact)
        );
        this.claim.inputs.fields = Array.from(
            document.querySelectorAll(
                claimInputsFields + ", " + claimInputsFieldsPayment
            )
        ).filter((field) => !field.closest("td.transaction"));
        this.claim.inputs.transactions = Array.from(
            document.querySelectorAll(claimInputsTransactions)
        );
    }

    static createInstance({
        appClaimForm,
        userLanguage,
        customInitClaim = null,
        customProcessClaim = null,
        customLoadMember = null,
        customDstProcess = null,
        errorHandler = new ErrorHandler(),
    }) {
        return new customSubmitClaim(
            appClaimForm,
            userLanguage,
            customInitClaim,
            customProcessClaim,
            customLoadMember,
            customDstProcess,
            errorHandler
        );
    }
}
